import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Building2, Phone, Mail, MapPin, Package, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

const businessTypes = [
  "Cafe",
  "Restaurant",
  "Otel",
  "Market / Bakkal",
  "E-Ticaret",
  "Distribütör",
  "Diğer",
];

const consumptionOptions = [
  "0-10 kg",
  "10-50 kg",
  "50-100 kg",
  "100-500 kg",
  "500+ kg",
];

export default function WholesalePage() {
  const [formData, setFormData] = useState({
    full_name: "",
    company_name: "",
    phone: "",
    email: "",
    city: "",
    business_type: "",
    monthly_consumption: "",
    message: "",
    inquiry_type: "wholesale",
  });
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/wholesale`, formData);
      toast.success("Talebiniz başarıyla gönderildi! En kısa sürede size ulaşacağız.");
      setFormData({
        full_name: "",
        company_name: "",
        phone: "",
        email: "",
        city: "",
        business_type: "",
        monthly_consumption: "",
        message: "",
        inquiry_type: "wholesale",
      });
      setStep(1);
    } catch (error) {
      toast.error("Bir hata oluştu. Lütfen tekrar deneyin.");
      console.error("Failed to submit:", error);
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (step === 1 && (!formData.full_name || !formData.company_name || !formData.email)) {
      toast.error("Lütfen zorunlu alanları doldurun.");
      return;
    }
    setStep(step + 1);
  };

  const prevStep = () => setStep(step - 1);

  return (
    <div className="pt-24" data-testid="wholesale-page">
      {/* Hero Section */}
      <section className="py-16 bg-[#0D3B2E]" data-testid="wholesale-hero">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="caption-text text-[#C5A059] mb-4 block">
              İş Ortaklığı
            </span>
            <h1 className="heading-xl text-[#F9F7F2]">Toptan Satış</h1>
            <p className="body-text text-[#F9F7F2]/70 mt-4 max-w-xl">
              Cafe, otel, restaurant ve işletmeler için özel fiyatlar ve
              ayrıcalıklı hizmetler.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-[#F9F7F2] border-b border-[#E5E0D8]">
        <div className="section-container">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { icon: Package, text: "Özel Fiyatlandırma" },
              { icon: Building2, text: "Kurumsal Destek" },
              { icon: MessageSquare, text: "Numune Gönderimi" },
              { icon: MapPin, text: "Tüm Türkiye'ye Teslimat" },
            ].map((item, index) => (
              <motion.div
                key={index}
                {...fadeUp}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-4"
              >
                <item.icon className="w-8 h-8 text-[#C5A059]" strokeWidth={1.5} />
                <span className="body-text text-[#0D3B2E]">{item.text}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="section-spacing bg-[#F9F7F2]" data-testid="wholesale-form-section">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left - Info */}
            <motion.div {...fadeUp}>
              <h2 className="heading-lg text-[#0D3B2E] mb-6">
                İş Ortağımız Olun
              </h2>
              <div className="decorative-line mb-8" />
              <p className="body-text text-[#5D4037] mb-8">
                SümbülAğa olarak iş ortaklarımıza özel fiyatlandırma, eğitim
                desteği ve pazarlama materyalleri sunuyoruz. Formu doldurun,
                sizinle en kısa sürede iletişime geçelim.
              </p>

              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <Phone className="w-5 h-5 text-[#C5A059]" strokeWidth={1.5} />
                  <span className="body-text text-[#5D4037]">+90 549 351 71 21</span>
                </div>
                <div className="flex items-center gap-4">
                  <Mail className="w-5 h-5 text-[#C5A059]" strokeWidth={1.5} />
                  <span className="body-text text-[#5D4037]">toptan@sumbulaga.com</span>
                </div>
              </div>
            </motion.div>

            {/* Right - Form */}
            <motion.div {...fadeUp}>
              <div className="bg-white p-8 md:p-12 border border-[#E5E0D8]">
                {/* Progress Indicator */}
                <div className="flex items-center justify-center gap-4 mb-10">
                  {[1, 2, 3].map((s) => (
                    <div
                      key={s}
                      className={`progress-dot ${
                        step === s ? "active" : step > s ? "completed" : ""
                      }`}
                    />
                  ))}
                </div>

                <form onSubmit={handleSubmit}>
                  {/* Step 1: Contact Info */}
                  {step === 1 && (
                    <div className="space-y-6" data-testid="form-step-1">
                      <h3 className="heading-md text-[#0D3B2E] mb-6">
                        İletişim Bilgileri
                      </h3>
                      <div>
                        <input
                          type="text"
                          name="full_name"
                          value={formData.full_name}
                          onChange={handleChange}
                          placeholder="Ad Soyad *"
                          className="input-premium w-full"
                          required
                          data-testid="input-full-name"
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          name="company_name"
                          value={formData.company_name}
                          onChange={handleChange}
                          placeholder="Firma Adı *"
                          className="input-premium w-full"
                          required
                          data-testid="input-company-name"
                        />
                      </div>
                      <div>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="E-posta *"
                          className="input-premium w-full"
                          required
                          data-testid="input-email"
                        />
                      </div>
                      <div>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="Telefon"
                          className="input-premium w-full"
                          data-testid="input-phone"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={nextStep}
                        className="btn-primary w-full"
                        data-testid="next-step-1"
                      >
                        Devam Et
                      </button>
                    </div>
                  )}

                  {/* Step 2: Business Info */}
                  {step === 2 && (
                    <div className="space-y-6" data-testid="form-step-2">
                      <h3 className="heading-md text-[#0D3B2E] mb-6">
                        İşletme Bilgileri
                      </h3>
                      <div>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          placeholder="Şehir"
                          className="input-premium w-full"
                          data-testid="input-city"
                        />
                      </div>
                      <div>
                        <select
                          name="business_type"
                          value={formData.business_type}
                          onChange={handleChange}
                          className="input-premium w-full bg-transparent cursor-pointer"
                          data-testid="select-business-type"
                        >
                          <option value="">İşletme Türü Seçin</option>
                          {businessTypes.map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <select
                          name="monthly_consumption"
                          value={formData.monthly_consumption}
                          onChange={handleChange}
                          className="input-premium w-full bg-transparent cursor-pointer"
                          data-testid="select-consumption"
                        >
                          <option value="">Aylık Tahmini Tüketim</option>
                          {consumptionOptions.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="flex gap-4">
                        <button
                          type="button"
                          onClick={prevStep}
                          className="btn-outline flex-1"
                          data-testid="prev-step-2"
                        >
                          Geri
                        </button>
                        <button
                          type="button"
                          onClick={nextStep}
                          className="btn-primary flex-1"
                          data-testid="next-step-2"
                        >
                          Devam Et
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Step 3: Message & Submit */}
                  {step === 3 && (
                    <div className="space-y-6" data-testid="form-step-3">
                      <h3 className="heading-md text-[#0D3B2E] mb-6">
                        Talep Detayları
                      </h3>
                      <div>
                        <div className="flex gap-4 mb-6">
                          <button
                            type="button"
                            onClick={() =>
                              setFormData((prev) => ({
                                ...prev,
                                inquiry_type: "wholesale",
                              }))
                            }
                            className={`flex-1 py-3 border transition-all ${
                              formData.inquiry_type === "wholesale"
                                ? "border-[#0D3B2E] bg-[#0D3B2E] text-white"
                                : "border-[#E5E0D8] text-[#5D4037]"
                            }`}
                            data-testid="type-wholesale"
                          >
                            Teklif Al
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setFormData((prev) => ({
                                ...prev,
                                inquiry_type: "sample",
                              }))
                            }
                            className={`flex-1 py-3 border transition-all ${
                              formData.inquiry_type === "sample"
                                ? "border-[#0D3B2E] bg-[#0D3B2E] text-white"
                                : "border-[#E5E0D8] text-[#5D4037]"
                            }`}
                            data-testid="type-sample"
                          >
                            Numune Talep Et
                          </button>
                        </div>
                      </div>
                      <div>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Mesajınız (opsiyonel)"
                          rows={4}
                          className="input-premium w-full resize-none"
                          data-testid="input-message"
                        />
                      </div>
                      <div className="flex gap-4">
                        <button
                          type="button"
                          onClick={prevStep}
                          className="btn-outline flex-1"
                          data-testid="prev-step-3"
                        >
                          Geri
                        </button>
                        <button
                          type="submit"
                          disabled={loading}
                          className="btn-gold flex-1 disabled:opacity-50"
                          data-testid="submit-wholesale"
                        >
                          {loading ? (
                            <span className="spinner mx-auto" />
                          ) : (
                            "Gönder"
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
